import {
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Link,
} from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { THEME } from '../../../global'
import { useAuth, useInputText } from '../../../hooks'
import payments from '../../../services/payments'
import { useEffectOnce } from 'react-use'
import { Header, Logo } from '../../../components'

export default function CheckOut() {
  const { user } = useAuth()
  const [processPayment, setProcessPayment] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const { price } = useParams()
  const initialValues = { phoneNumber: user?.phoneNumber }
  const input = useInputText(initialValues)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setProcessPayment(true)

    try {
      if (price && input.phoneNumber)
        await payments.pay({ phoneNumber: input.phoneNumber, amount: price })

      setIsSuccess(true)
    } catch (err) {
      setIsError(true)
    } finally {
      setProcessPayment(false)
    }
  }

  useEffectOnce(() => {
    return () => {
      setIsError(false)
      setIsSuccess(false)
    }
  })

  if (isSuccess) return <ProcessPaymentSuccess />
  if (isError) return <ProcessPaymentError />

  return (
    <>
      {!processPayment ? (
        <Container component="main">
          <CssBaseline />
          <Header />
          <div className="flex w-full flex-col items-center">
            <div className="my-8">
              <Logo />
            </div>

            <div className="flex items-end my-6">
              <p className="text-6xl text-primary font-black">{price}</p>
              <span className="text-md">.00Mt</span>
            </div>

            <p className="text-center max-w-[85%] my-6">
              Introduza o seu número de telefone com conta M-Pesa!
            </p>

            <form
              className="flex flex-col items-center w-full"
              onSubmit={handleSubmit}
            >
              <div className="flex bg-disabled items-center mb-6 w-full max-w-[600px] px-2 py-4 rounded-lg">
                <p className="text-2xl text-tertiary font-black pr-1 border-r border-tertiary">
                  +258
                </p>
                <input
                  type="number"
                  className="w-full h-8 p-2 font-black text-2xl bg-transparent focus:outline-none"
                />
              </div>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  bgcolor: THEME.colors.success,
                  borderRadius: 2,
                  mt: 2,
                  mb: 2,
                  width: '70%',
                  height: 46,
                }}
              >
                <p className="text-lg capitalize ">Comprar</p>
              </Button>
            </form>

            <p className="text-center text-[#A3A3A3] my-6">
              A Vodacom enviará uma notificação USSD para o seu celular. Para
              confirmar a compra, introduza o seu PIN.
            </p>
          </div>
        </Container>
      ) : (
        <ProcessPaymentLoader phoneNumber={input.phoneNumber} />
      )}
    </>
  )
}

function ProcessPaymentLoader({ phoneNumber }: { phoneNumber?: string }) {
  return (
    <Container component="main">
      <CssBaseline />
      <Header />

      <div className="flex w-full flex-col items-center">
        <div className="my-8">
          <Logo />
        </div>
        <div className="my-8">
          <CircularProgress size={80} />
        </div>

        <p className="text-center font-black my-2">
          O seu pagamento está a ser processado.
        </p>

        <p className="text-center  text-sm my-2">
          Por favor, verifique o seu celular!
        </p>

        <p className="text-center text-[#A3A3A3] w-[90%] my-2">
          Foi enviada uma notificação USSD para o seu celular com o número
          {phoneNumber}. Insira o seu PIN para confirmar o pagamento.
        </p>
      </div>
    </Container>
  )
}

function ProcessPaymentSuccess() {
  const navigate = useNavigate()

  return (
    <Container component="main">
      <CssBaseline />

      <div className="flex w-full flex-col items-center">
        <div className="mt-[25%] mb-6">
          <img src="/payment-success-icon.svg" alt="success icon" />
        </div>

        <p className="text-center text-2xl font-black my-2">
          Pagamento realizado com sucesso.
        </p>

        <p className="text-center font-black my-2">
          Parabéns! Agora você tem acesso aos benefícios da plataforma.
        </p>

        <Button
          type="button"
          onClick={() => navigate('/user/')}
          fullWidth
          variant="contained"
          sx={{
            bgcolor: THEME.colors.primary,
            borderRadius: 2,
            mt: 2,
            mb: 2,
            width: '70%',
            height: 46,
          }}
        >
          <p className="text-lg capitalize ">Entrar</p>
        </Button>
      </div>
    </Container>
  )
}

function ProcessPaymentError() {
  const navigate = useNavigate()

  return (
    <Container component="main">
      <CssBaseline />
      <Header />

      <div className="flex w-full flex-col items-center">
        <div className="mt-[25%] mb-6">
          <img src="/payment-error-icon.svg" alt="error icon" />
        </div>

        <p className="text-center text-2xl font-black my-2">
          Pagamento não realizado!
        </p>

        <p className="text-center text-sm my-4">
          Ocorreu um erro ao tentar processar o seu pagamento. Por favor, volte
          a tentar mais tarde.
        </p>

        <Button
          type="button"
          onClick={() => navigate(-1)}
          fullWidth
          variant="contained"
          sx={{
            bgcolor: THEME.colors.primary,
            borderRadius: 2,
            mt: 2,
            mb: 2,
            width: '70%',
            height: 46,
          }}
        >
          <p className="text-lg capitalize ">Tentar novamente</p>
        </Button>

        <Link
          onClick={() => navigate('/user/')}
          sx={{ fontSize: 13, color: 'white' }}
        >
          Sair sem pagar o acesso à plataforma
        </Link>
      </div>
    </Container>
  )
}
